import { parseLetterAppointmentContext } from "../../../../../_shared/_logic/letter-table-tags"
import {
  parseWorkOrderPrintPreviewRow,
  previewOptionalString,
  previewPrintMode,
  previewRecord,
  previewRequiredString,
  previewStringMap,
} from "../../../_shared/_logic/preview-context"
import type { WorkOrderPrintPreviewRow } from "../../../../../../../../../../../../../api/kehrblatt/letters"
import type { LetterSelectionPreviewPayload, LetterSelectionPreviewRowContext } from "../contract"

const PREVIEW_KEYS = new Set([
  "row_context_by_action_case_id",
  "row_errors_by_action_case_id",
  "shared",
])
const ROW_CONTEXT_KEYS = new Set([
  "appointment",
  "communication_presentation",
  "letter_delivery_batch_id",
  "letter_delivery_batch_item_id",
  "contact_order_step_id",
  "work_order_id",
  "schedule_event_id",
  "status",
  "print_preview_id",
  "current_print_mode",
  "current_recipient_source",
  "current_recipient_name",
  "current_recipient_customer_id",
  "current_recipient_contact_point_id",
  "recipient_review_reason",
])

export const LETTER_SELECTION_PREVIEW_KIND =
  "letter_delivery_batch.select_appointment_letters_for_delivery.standard_table"

const parseRowContext = (value: unknown, path: string): LetterSelectionPreviewRowContext => {
  const source = previewRecord(value)

  if (Object.keys(source).some((key) => !ROW_CONTEXT_KEYS.has(key))) {
    throw new Error(`${path} enthält unbekannte Felder.`)
  }

  const batchId = previewRequiredString(source.letter_delivery_batch_id)
  const itemId = previewRequiredString(source.letter_delivery_batch_item_id)
  const contactOrderStepId = previewRequiredString(source.contact_order_step_id)
  const workOrderId = previewRequiredString(source.work_order_id)
  const status = previewRequiredString(source.status)
  const printPreviewId = previewRequiredString(source.print_preview_id)
  const currentPrintMode = previewPrintMode(source.current_print_mode)
  const currentRecipientSource = previewRequiredString(source.current_recipient_source)

  if (
    !batchId ||
    !itemId ||
    !contactOrderStepId ||
    !workOrderId ||
    !status ||
    !printPreviewId ||
    !currentPrintMode ||
    !["CUSTOMER_CONTACT_ADDRESS", "OBJECT_ADDRESS", "IMPERSONAL"].includes(currentRecipientSource)
  ) {
    throw new Error(`${path} ist unvollständig oder ungültig.`)
  }

  const optionalString = (key: string) => {
    const parsed = previewOptionalString(source[key])

    if (source[key] != null && !parsed)
      throw new Error(`${path}.${key} muss String oder null sein.`)

    return parsed
  }
  const scheduleEventId = optionalString("schedule_event_id")
  const currentRecipientCustomerId = optionalString("current_recipient_customer_id")
  const currentRecipientContactPointId = optionalString("current_recipient_contact_point_id")
  const currentRecipientName = optionalString("current_recipient_name")

  if (
    (currentRecipientSource === "CUSTOMER_CONTACT_ADDRESS" &&
      (!currentRecipientCustomerId || !currentRecipientContactPointId)) ||
    (currentRecipientSource === "OBJECT_ADDRESS" &&
      (!currentRecipientCustomerId || currentRecipientContactPointId || !currentRecipientName)) ||
    (currentRecipientSource === "IMPERSONAL" &&
      (currentRecipientCustomerId || currentRecipientContactPointId))
  ) {
    throw new Error(`${path} enthält eine unvollständige Empfängerreferenz.`)
  }

  return {
    ...parseLetterAppointmentContext(source),
    letter_delivery_batch_id: batchId,
    letter_delivery_batch_item_id: itemId,
    contact_order_step_id: contactOrderStepId,
    work_order_id: workOrderId,
    schedule_event_id: scheduleEventId,
    status,
    print_preview_id: printPreviewId,
    current_print_mode: currentPrintMode,
    current_recipient_source:
      currentRecipientSource as LetterSelectionPreviewRowContext["current_recipient_source"],
    current_recipient_name: currentRecipientName,
    current_recipient_customer_id: currentRecipientCustomerId,
    current_recipient_contact_point_id: currentRecipientContactPointId,
    recipient_review_reason: optionalString("recipient_review_reason"),
  }
}

export const parseLetterSelectionPreview = (value: unknown): LetterSelectionPreviewPayload => {
  const source = previewRecord(value)

  if (Object.keys(source).some((key) => !PREVIEW_KEYS.has(key))) {
    throw new Error("Der Terminbrief-Auswahl-Preview enthält unbekannte Felder.")
  }

  const rawContexts = previewRecord(source.row_context_by_action_case_id)
  const rowContextByActionCaseId: LetterSelectionPreviewPayload["row_context_by_action_case_id"] =
    {}
  for (const [actionCaseId, context] of Object.entries(rawContexts)) {
    rowContextByActionCaseId[actionCaseId] = parseRowContext(
      context,
      `row_context_by_action_case_id.${actionCaseId}`,
    )
  }
  const shared = previewRecord(source.shared)

  if (Object.keys(shared).some((key) => key !== "print_previews_by_id")) {
    throw new Error("Der Terminbrief-Auswahl-Preview enthält unbekannte Shared-Daten.")
  }

  const rawPrintPreviews = previewRecord(shared.print_previews_by_id)
  const printPreviewsById: LetterSelectionPreviewPayload["shared"]["print_previews_by_id"] = {}
  for (const [previewId, preview] of Object.entries(rawPrintPreviews)) {
    const parsed = parseWorkOrderPrintPreviewRow(preview)

    if (!parsed) throw new Error(`shared.print_previews_by_id.${previewId} ist ungültig.`)

    printPreviewsById[previewId] = parsed
  }
  for (const [actionCaseId, context] of Object.entries(rowContextByActionCaseId)) {
    const printPreview = printPreviewsById[context.print_preview_id]

    if (!printPreview) {
      throw new Error(
        `row_context_by_action_case_id.${actionCaseId}.print_preview_id referenziert die fehlende ID ${context.print_preview_id}.`,
      )
    }

    if (printPreview.work_order_id !== context.work_order_id) {
      throw new Error(
        `row_context_by_action_case_id.${actionCaseId}.work_order_id passt nicht zur Druckvorschau.`,
      )
    }

    if (
      context.current_recipient_source === "CUSTOMER_CONTACT_ADDRESS" &&
      !printPreview.recipient_options.some(
        (option) =>
          option.customer_id === context.current_recipient_customer_id &&
          option.contact_point_id === context.current_recipient_contact_point_id,
      )
    ) {
      throw new Error(
        `row_context_by_action_case_id.${actionCaseId} referenziert einen fehlenden Empfänger.`,
      )
    }

    if (
      context.current_recipient_source === "OBJECT_ADDRESS" &&
      !completeAddress(printPreview.property_address)
    ) {
      throw new Error(
        `row_context_by_action_case_id.${actionCaseId} referenziert eine unvollständige Objektadresse.`,
      )
    }
  }

  return {
    row_context_by_action_case_id: rowContextByActionCaseId,
    row_errors_by_action_case_id: previewStringMap(source.row_errors_by_action_case_id),
    shared: { print_previews_by_id: printPreviewsById },
  }
}

const completeAddress = (address: WorkOrderPrintPreviewRow["property_address"]) =>
  Boolean(address?.country && address.zip && address.city && address.street && address.house_number)
